import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Alert, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import formatMinutesToReadable from "../../reusableComps/formatDays/formatDays";
import ConfirmationModal from "../../reusableComps/ConfirmationModal/ConfirmationModal";
import isOverlappingAbsence from "../../reusableComps/isOverlappingAbsence/isOverlappingAbsence";
import { useSelector, useDispatch } from "react-redux";
import { fetchOrganizationSettings } from "../../../redux/slices/organizationSettingsSlice";

const countWorkingDays = (startDate, endDate) => {
  let count = 0;
  let current = new Date(startDate);

  while (current <= endDate) {
    const day = current.getDay();

    // 0 = Sunday, 6 = Saturday
    if (day !== 0 && day !== 6) {
      count++;
    }

    current.setDate(current.getDate() + 1);
  }

  return count;
};

const RequestAbsenceModal = ({
  show,
  handleClose,
  newAbsence,
  handleInputChange,
  handleRequestAbsence,
  setNewAbsence,
  balance,
  editMode = false,
  validationError,
  setValidationError,
  absences,
  absenceToEdit,
  userId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [mode, setMode] = useState("multiple");
  const [useVacationDays, setUseVacationDays] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);

  const user = useSelector((state) => state.auth.user);

  const orgSettings = useSelector((state) => state.organization?.item);

  useEffect(() => {
    if (show && !orgSettings) {
      dispatch(fetchOrganizationSettings());
    }
  }, [show, orgSettings, dispatch]);

  const allowedAbsenceTypes = useMemo(
    () =>
      orgSettings?.default_absence_types?.length > 0
        ? orgSettings.default_absence_types.map((type) =>
          typeof type === "string" ? { key: type, label: type } : type,
        )
        : [
          { key: "vacation", label: "Férias" },
          { key: "sick leave", label: "Baixa Médica" },
          { key: "personal leave", label: "Licença Pessoal" },
          { key: "other", label: "Outro" },
        ],
    [orgSettings],
  );

  const isAdminCreatingForOtherUser = userId && userId !== user?.id;

  useEffect(() => {
    if (show) {
      if (newAbsence.is_half_day) setMode("half");
      else if (newAbsence.is_multiple_days) setMode("multiple");
      else setMode("single");

      if (!newAbsence.absence_type && allowedAbsenceTypes.length > 0) {
        setNewAbsence((prev) => ({ ...prev, absence_type: allowedAbsenceTypes[0].key }));
      }
    }
  }, [show, newAbsence.is_half_day, newAbsence.is_multiple_days, allowedAbsenceTypes, newAbsence.absence_type, setNewAbsence]);

  const isVacationType = (type) => {
    if (!type) return false;
    const lower = type.toLowerCase();
    return lower.includes("férias") || lower.includes("vacation");
  };

  const isMedicalLeaveType = (type) => {
    if (!type) return false;
    const lower = type.toLowerCase();
    return lower.includes("sick") || lower.includes("doença") || lower.includes("medical") || lower.includes("doenca");
  };

  useEffect(() => {
    if (isVacationType(newAbsence.absence_type)) {
      setUseVacationDays(false);
    }
  }, [newAbsence.absence_type]);

  useEffect(() => {
    if (!show) {
      setMode("multiple");
      setValidationError("");
    }
  }, [show, setValidationError]);

  useEffect(() => {
    if (mode === "multiple" && newAbsence.start_date && newAbsence.end_date) {
      const start = new Date(newAbsence.start_date);
      const end = new Date(newAbsence.end_date);

      const workingDays = countWorkingDays(start, end);
      const totalMinutes = workingDays * 8 * 60;

      setNewAbsence((prev) => ({
        ...prev,
        total_minutes: totalMinutes > 0 ? totalMinutes : 0,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, newAbsence.start_date, newAbsence.end_date]);

  useEffect(() => {
    if ((mode === "single" || mode === "half") && newAbsence.start_date) {
      setNewAbsence((prev) => ({
        ...prev,
        total_minutes: 8 * 60,
        end_date: prev.start_date,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, newAbsence.start_date]);

  useEffect(() => {
    if (mode === "single") {
      setNewAbsence((prev) => ({
        ...prev,
        total_minutes: 8 * 60,
        end_date: prev.start_date,
      }));
    }

    if (isMedicalLeaveType(newAbsence.absence_type) && documentFile === null) {
      setValidationError(
        t("components.RequestAbsenceModal.errors.doctor_file"),
      );
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (mode === "half" && newAbsence.start_time && newAbsence.end_time) {
      const [startHour, startMinute] = newAbsence.start_time
        .split(":")
        .map(Number);
      const [endHour, endMinute] = newAbsence.end_time.split(":").map(Number);

      const startInMinutes = startHour * 60 + startMinute;
      const endInMinutes = endHour * 60 + endMinute;

      let durationInMinutes = endInMinutes - startInMinutes;
      if (durationInMinutes < 0) durationInMinutes = 0;

      setNewAbsence((prev) => ({
        ...prev,
        total_minutes: durationInMinutes,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    newAbsence.start_time,
    newAbsence.end_time,
    newAbsence.start_date,
    newAbsence.absence_type,
    documentFile,
    t,
    setValidationError
  ]);

  const validateDates = () => {
    if (mode === "half") {
      if (
        !newAbsence.start_date ||
        !newAbsence.start_time ||
        !newAbsence.end_time
      )
        return false;
      if (newAbsence.start_time === newAbsence.end_time) return false;
      const start = new Date(
        `${newAbsence.start_date}T${newAbsence.start_time}`,
      );
      const end = new Date(`${newAbsence.start_date}T${newAbsence.end_time}`);
      return end > start;
    } else if (mode === "multiple") {
      if (!newAbsence.start_date || !newAbsence.end_date) return false;
      const start = new Date(newAbsence.start_date);
      const end = new Date(newAbsence.end_date);
      return end > start;
    } else {
      if (!newAbsence.start_date || !newAbsence.end_date) return false;
      const start = new Date(newAbsence.start_date);
      const end = new Date(newAbsence.end_date);
      return end >= start;
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validateDates()) {
      setValidationError(
        mode === "half"
          ? t("components.RequestAbsenceModal.errors.time_order")
          : t("components.RequestAbsenceModal.errors.date_order"),
      );
      return;
    }
    const editingId = editMode && absenceToEdit ? absenceToEdit.id : null;
    if (isOverlappingAbsence(newAbsence, absences, editingId, user?.id)) {
      setValidationError(
        mode === "half"
          ? t("components.RequestAbsenceModal.errors.overlap_time")
          : t("components.RequestAbsenceModal.errors.overlap_date"),
      );
      return;
    }
    setValidationError("");

    const willUseVacation =
      isVacationType(newAbsence.absence_type) || useVacationDays;
    if (
      willUseVacation &&
      balance &&
      Number(balance.available_minutes) < Number(newAbsence.total_minutes)
    ) {
      setShowConfirm(true);
      return;
    }

    handleRequestAbsence(e, useVacationDays, false, documentFile);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    handleRequestAbsence({ preventDefault: () => { } }, useVacationDays, true, documentFile);
  };

  const handleFileChange = (e) => {
    setDocumentFile(e.target.files[0]);
  };

  const handleModeChange = (e) => {
    const value = e.target.value;
    setMode(value);
    if (value === "half") {
      setNewAbsence((prev) => ({
        ...prev,
        is_half_day: true,
        is_multiple_days: false,
        start_time: "",
        end_time: "",
        total_minutes: 4 * 60,
        end_date: prev.start_date,
      }));
    } else if (value === "multiple") {
      setNewAbsence((prev) => ({
        ...prev,
        is_half_day: false,
        is_multiple_days: true,
        start_time: "",
        end_time: "",
        total_minutes: "",
      }));
    } else {
      setNewAbsence((prev) => ({
        ...prev,
        is_half_day: false,
        is_multiple_days: false,
        start_time: "",
        end_time: "",
        total_minutes: 8 * 60,
        end_date: prev.start_date,
      }));
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        contentClassName="bg-surface border-0"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: "4px",
                height: "24px",
                backgroundColor: "var(--action-color)",
                borderRadius: "4px",
              }}
            ></div>
            <h4 className="modal-title mb-0 txt-main">
              {editMode
                ? t("components.RequestAbsenceModal.edit_absence")
                : isAdminCreatingForOtherUser
                  ? t("components.RequestAbsenceModal.assign_absence")
                  : t("components.RequestAbsenceModal.new_absence")}
            </h4>
          </div>
        </Modal.Header>

        <Modal.Body className="pt-3">
          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                {t("components.RequestAbsenceModal.request_type")}
              </Form.Label>
              <Form.Select
                value={mode}
                onChange={handleModeChange}
                style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
              >
                <option value="multiple">{t("components.RequestAbsenceModal.modes.multiple")}</option>
                <option value="single">{t("components.RequestAbsenceModal.modes.single")}</option>
                <option value="half">{t("components.RequestAbsenceModal.modes.half")}</option>
              </Form.Select>
            </Form.Group>

            {!isVacationType(newAbsence.absence_type) && (
              <Form.Group className="mb-3">
                <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                  {t("components.RequestAbsenceModal.use_vacation_days_question")}
                </Form.Label>
                <Form.Select
                  value={useVacationDays ? "yes" : "no"}
                  onChange={(e) => setUseVacationDays(e.target.value === "yes")}
                  style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
                >
                  <option value="no">{t("components.RequestAbsenceModal.no")}</option>
                  <option value="yes">{t("components.RequestAbsenceModal.yes")}</option>
                </Form.Select>
              </Form.Group>
            )}

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                    {t("components.RequestAbsenceModal.start_date")}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={newAbsence.start_date}
                    onChange={handleInputChange}
                    required
                    style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
                  />
                </Form.Group>
              </Col>
              {mode === "multiple" && (
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                      {t("components.RequestAbsenceModal.end_date")}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="end_date"
                      value={newAbsence.end_date}
                      onChange={handleInputChange}
                      required
                      style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            {mode === "half" && (
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                      {t("components.RequestAbsenceModal.start_time")}
                    </Form.Label>
                    <Form.Control
                      type="time"
                      name="start_time"
                      value={newAbsence.start_time || ""}
                      onChange={handleInputChange}
                      required
                      style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                      {t("components.RequestAbsenceModal.end_time")}
                    </Form.Label>
                    <Form.Control
                      type="time"
                      name="end_time"
                      value={newAbsence.end_time || ""}
                      onChange={handleInputChange}
                      required
                      style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {validationError && (
              <Alert variant="danger" className="mt-2" style={{ borderRadius: "var(--btn-radius)" }}>
                {validationError}
              </Alert>
            )}

            {(mode === "multiple" || mode === "single" || mode === "half") && (
              <Form.Group className="mb-3">
                <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                  {mode === "half" ? t("components.RequestAbsenceModal.total_time") : t("components.RequestAbsenceModal.total_days")}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formatMinutesToReadable(newAbsence.total_minutes)}
                  disabled
                  style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)", backgroundColor: "var(--gray-50)" }}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                {t("components.RequestAbsenceModal.reason")}
              </Form.Label>
              <Form.Control
                as="textarea"
                name="reason"
                value={newAbsence.reason}
                onChange={handleInputChange}
                style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                {t("components.RequestAbsenceModal.absence_type")}
              </Form.Label>
              <Form.Select
                name="absence_type"
                value={newAbsence.absence_type}
                onChange={handleInputChange}
                required
                style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
              >
                {allowedAbsenceTypes.map(({ key, label }) => (
                  <option key={key} value={key}>
                    {t(`components.RequestAbsenceModal.types.${key.replace(/\s+/g, "_")}`, { defaultValue: label })}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {isMedicalLeaveType(newAbsence.absence_type) && (
              <Form.Group className="mb-4">
                <Form.Label className="txt-subtle mb-1" style={{ fontSize: "var(--text-small)", fontWeight: "600" }}>
                  {t("components.RequestAbsenceModal.medical_proof_label")}
                </Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ borderRadius: "var(--btn-radius)", border: "1px solid var(--gray-300)" }}
                />
              </Form.Group>
            )}

            <div className="d-flex justify-content-end gap-3 mt-4">
              <Button
                variant="light"
                onClick={handleClose}
                className="px-2 py-2"
                style={{
                  borderRadius: "var(--btn-radius)",
                  fontWeight: "600"
                }}
              >
                {t("components.RequestAbsenceModal.cancel")}
              </Button>
              <Button
                type="submit"
                className="px-2 py-2"
                style={{
                  backgroundColor: "var(--brand-primary)",
                  borderColor: "var(--brand-primary)",
                  borderRadius: "var(--btn-radius)",
                  fontWeight: "600",
                  whiteSpace: "nowrap"
                }}
              >
                {editMode ? t("components.RequestAbsenceModal.save_changes") : t("components.RequestAbsenceModal.submit_request")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <ConfirmationModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        title={t("components.RequestAbsenceModal.confirm_modal.title")}
        message={t("components.RequestAbsenceModal.confirm_modal.message")}
      />
    </>
  );
};

export default RequestAbsenceModal;